import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, Minus, Plus, ShoppingBag, Trash2, X } from "lucide-react";
import { PublicLayout } from "../../components/PublicLayout";
import { useCart } from "../../hooks/useCart";
import { MXN } from "../../lib/apiClient";

const formatValue = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).join(", ");
  if (value && typeof value === "object") return Object.values(value).filter(Boolean).join(", ");
  return value;
};

const getItemDetails = (customization = {}) => ([
  ["Tamaño / porciones", customization.tamano],
  ["Sabor", customization.sabor],
  ["Relleno", customization.relleno],
  ["Decoración", customization.decoracion],
  ["Extras / toppings", customization.extras || customization.toppings],
  ["Mensaje / dedicatoria", customization.mensaje || customization.dedicatoria],
  ["Comentarios", customization.comentarios || customization.notas],
  ["Referencia", customization.reference_file_name || customization.referencia],
]).map(([label, value]) => [label, formatValue(value)])
  .filter(([, value]) => value);

export default function Cart() {
  const navigate = useNavigate();
  const { items, removeItem, updateQuantity, clearCart, subtotal, count } = useCart();
  const [expandedItems, setExpandedItems] = useState({});
  const repeatedProducts = useMemo(() => items.reduce((acc, item) => {
    acc[item.product_id] = (acc[item.product_id] || 0) + 1;
    return acc;
  }, {}), [items]);

  const toggleDetails = (cartId) => {
    setExpandedItems((current) => ({ ...current, [cartId]: !current[cartId] }));
  };

  return (
    <PublicLayout>
      <div className="max-w-5xl mx-auto px-5 sm:px-6 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-7">
          <div>
            <p className="text-dorado italic font-serif text-xl">tu selección dulce</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-vino leading-none">Carrito</h1>
            <p className="text-cafe/60 text-sm mt-2">
              Revisa todo lo que agregaste. Al final te pediremos entrega y tus datos.
            </p>
          </div>
          {items.length > 0 && (
            <button onClick={clearCart} className="self-start sm:self-auto inline-flex items-center gap-2 text-sm text-vino/70 hover:text-vino">
              <Trash2 className="w-4 h-4" /> Vaciar carrito
            </button>
          )}
        </div>

        {items.length === 0 ? (
          <div className="bg-blancoCalido border border-beige rounded-[32px] p-8 sm:p-12 text-center shadow-pastel">
            <div className="w-20 h-20 mx-auto rounded-full bg-beige/70 flex items-center justify-center text-vino mb-4">
              <ShoppingBag className="w-9 h-9" />
            </div>
            <h2 className="font-serif text-3xl text-vino">Tu carrito está vacío</h2>
            <p className="text-cafe/60 mt-2 mb-6">Agrega tus favoritos del catálogo y vuelve aquí para cerrar tu pedido.</p>
            <Link to="/catalogo" className="inline-flex bg-vino text-crema rounded-full px-7 py-3.5 font-medium hover:bg-vinoDark">
              Ver catálogo
            </Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
            <div className="space-y-3">
              {items.map((item) => {
                const details = getItemDetails(item.customization);
                const isExpanded = !!expandedItems[item.cart_id];
                const shortDetails = details.slice(0, 3).map(([, value]) => value).join(" · ");

                return (
                  <div key={item.cart_id} className="bg-blancoCalido border border-beige rounded-[28px] p-3 sm:p-4 shadow-pastel">
                    <div className="flex gap-3 sm:gap-4">
                      <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-beige overflow-hidden shrink-0">
                        {item.image ? <img src={item.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-beige" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-serif text-xl sm:text-2xl text-vino leading-tight truncate">{item.product_name}</p>
                            <p className="text-xs text-cafe/50 mt-1">
                              {item.requires_quote ? "Por cotizar" : `${MXN(item.base_price)} c/u`}
                            </p>
                            <p className="text-xs text-cafe/45 mt-1 line-clamp-2">
                              {shortDetails || "Sin personalización adicional"}
                            </p>
                            {repeatedProducts[item.product_id] > 1 && (
                              <span className="inline-flex mt-2 text-[11px] rounded-full bg-rosa/20 text-vino px-2.5 py-1">
                                Variante personalizada
                              </span>
                            )}
                          </div>
                          <button onClick={() => removeItem(item.cart_id)} className="h-9 w-9 rounded-full hover:bg-beige text-vino/60 flex items-center justify-center shrink-0" aria-label="Quitar producto">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex items-center justify-between gap-3 mt-4">
                          <div className="flex items-center gap-2">
                            <button onClick={() => updateQuantity(item.cart_id, item.quantity - 1)} className="w-9 h-9 rounded-full bg-beige text-vino flex items-center justify-center">
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="font-serif text-xl text-vino w-8 text-center">{item.quantity}</span>
                            <button onClick={() => updateQuantity(item.cart_id, item.quantity + 1)} className="w-9 h-9 rounded-full bg-vino text-crema flex items-center justify-center">
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                          <p className="font-serif text-xl sm:text-2xl text-vino">
                            {item.requires_quote ? "Cotización" : MXN(Number(item.base_price || 0) * Number(item.quantity || 1))}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 border-t border-beige/80 pt-3">
                      <button
                        type="button"
                        onClick={() => toggleDetails(item.cart_id)}
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-vino hover:text-vinoDark"
                      >
                        {isExpanded ? "Ocultar detalles" : "Ver detalles"}
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>

                      {isExpanded && (
                        <div className="mt-3 rounded-3xl bg-crema/70 border border-beige/80 p-4">
                          {details.length > 0 ? (
                            <div className="grid sm:grid-cols-2 gap-3">
                              {details.map(([label, value]) => (
                                <div key={label}>
                                  <p className="text-[11px] uppercase tracking-[0.14em] text-cafe/40">{label}</p>
                                  <p className="text-sm text-cafe mt-0.5">{value}</p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-cafe/55">Este producto no tiene personalización adicional.</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <aside className="bg-blancoCalido border border-beige rounded-[30px] p-5 sm:p-6 shadow-pastel sticky top-24">
              <p className="font-serif text-2xl text-vino">Resumen</p>
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between gap-4">
                  <span className="text-cafe/60">Productos</span>
                  <span className="text-cafe">{count}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-cafe/60">Subtotal</span>
                  <span className="font-serif text-xl text-vino">{subtotal ? MXN(subtotal) : "Por cotizar"}</span>
                </div>
              </div>
              <button onClick={() => navigate("/pedido?checkout=1")} className="mt-5 w-full bg-vino text-crema rounded-full py-4 font-semibold hover:bg-vinoDark transition-colors">
                Finalizar pedido
              </button>
              <Link to="/catalogo" className="mt-3 w-full border-2 border-vino text-vino rounded-full py-3.5 font-medium hover:bg-vino/5 transition-colors inline-flex justify-center">
                Seguir comprando
              </Link>
            </aside>
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
